import { Form, Formik, FormikHelpers } from "formik";
import { useState } from "react";
import { opretStamdataaendringBasis } from "../../api/stamdata";
import { ApiRequestError } from "../../api/util";
import { PersonStamdata } from "../../interfaces/stamdata";
import { PersonStamdataAendringBasis } from "../../interfaces/stamdata";
import { hasPendingStamdataSection } from "../../lib/stamdataPendingChange";
import { accountBasisValidationSchema } from "../../util/validationSchema/accountBasis";
import { AccordionBody, AccordionHeader, AccordionItem } from "../Accordion";
import { Button } from "../Button";
import { FieldWithLabel } from "../form/FieldWithLabel";
import Spinner from "../graphics/Spinner";

const getSubmitErrorMessage = (error: unknown) => {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Der opstod en fejl. Prøv igen senere.";
};

const AccountBasis = ({ stamdata }: { stamdata: PersonStamdata | null }) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const initialValues: PersonStamdataAendringBasis = {
    email: stamdata?.email ?? "",
    telefonMobilNr: stamdata?.telefonMobilNr ?? "",
  };

  const hasSectionPendingChange =
    stamdata != null && hasPendingStamdataSection(stamdata, "basis");
  const showPendingAlert = hasSectionPendingChange && !submitSuccess;
  const isFormLocked = hasSectionPendingChange || submitSuccess;

  const handleSubmit = async (
    values: PersonStamdataAendringBasis,
    { resetForm }: FormikHelpers<PersonStamdataAendringBasis>,
  ) => {
    if (!stamdata || isFormLocked) return;

    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      await opretStamdataaendringBasis({
        personId: stamdata.id,
        email: values.email,
        telefonMobilNr: values.telefonMobilNr,
      });
      resetForm({ values });
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(getSubmitErrorMessage(error));
    }
  };

  return (
    <AccordionItem value="kontakt">
      <AccordionHeader>Person- og kontaktoplysninger</AccordionHeader>
      <AccordionBody>
        {!stamdata ? (
          <div className="alert alert-danger mb-0" role="alert">
            Kunne ikke hente dine oplysninger. Prøv at genindlæse siden.
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={accountBasisValidationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ dirty, isSubmitting, errors, touched }) => (
              <Form>
                {showPendingAlert ? (
                  <div className="alert alert-info mb-4" role="status">
                    Du har allerede en aktiv anmodning om ændring af dine
                    oplysninger. Den behandles af Advokatsamfundet.
                  </div>
                ) : null}

                {submitError ? (
                  <div
                    className="alert alert-danger d-flex align-items-start justify-content-between gap-3 mb-4"
                    role="alert"
                  >
                    <span>{submitError}</span>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Luk fejlmeddelelse"
                      onClick={() => setSubmitError(null)}
                    />
                  </div>
                ) : null}

                {submitSuccess ? (
                  <div
                    className="alert alert-success d-flex align-items-start justify-content-between gap-3 mb-4"
                    role="alert"
                  >
                    <span>Din anmodning om ændring er sendt.</span>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Luk bekræftelse"
                      onClick={() => setSubmitSuccess(false)}
                    />
                  </div>
                ) : null}

                <fieldset disabled={isFormLocked}>
                  <div className="row">
                    <div className="col">
                      <FieldWithLabel
                        fieldId="email"
                        fieldName="email"
                        fieldType="email"
                        label="E-mail"
                        placeholder="E-mail"
                        touched={touched.email}
                        error={errors.email}
                      />
                    </div>
                    <div className="col">
                      <FieldWithLabel
                        fieldId="telefonMobilNr"
                        fieldName="telefonMobilNr"
                        fieldType="tel"
                        label="Mobiltelefon"
                        placeholder="Mobiltelefon"
                        touched={touched.telefonMobilNr}
                        error={errors.telefonMobilNr}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <Button
                      id="account-basis-submit"
                      disabled={!dirty || isFormLocked || isSubmitting}
                      className="btn btn-lg btn-primary mt-4"
                      type="submit"
                      aria-label="Gem kontaktoplysninger"
                    >
                      {isSubmitting ? (
                        <span className="d-inline-flex align-items-center gap-2">
                          <Spinner inline size={16} label="" />
                          Gemmer...
                        </span>
                      ) : (
                        "Gem"
                      )}
                    </Button>
                  </div>
                </fieldset>
              </Form>
            )}
          </Formik>
        )}
      </AccordionBody>
    </AccordionItem>
  );
};

export default AccountBasis;
